import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { AssetDto } from '@picflow/types';
import { catchError, firstValueFrom, map, throwError, timer } from 'rxjs';
import { getFileContentType } from '../lib/get-file-type';
import { loadScript } from '../lib/load-script';
import { PICFLOW_CONFIG } from '../picflow-config';
import { DataLoaderService } from './data-loader.service';

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    gapi: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    google: any;
  }
}

export interface GoogleDriveFile {
  name: string;
  id: string;
  size: number;
  source: 'google-drive';
}

const API_KEY = 'AIzaSyA3QtG-HpoiklX8jid6_AriI9ToWlEbzfg';
const SCOPES = 'https://www.googleapis.com/auth/drive.readonly';
const CLIENT_ID = '387343500342-obn0crc436d0kubai59g2frmat1sbmvl.apps.googleusercontent.com';

@Injectable({
  providedIn: 'root',
})
export class GoogleDriveService {
  private http = inject(HttpClient);
  private config = inject(PICFLOW_CONFIG);
  private loader = inject(DataLoaderService);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  tokenClient: any;
  accessToken: string;

  private async loadGoogleClient() {
    await this.loader.get('google-drive-client', async () => {
      await loadScript('https://apis.google.com/js/api.js');
      await loadScript('https://accounts.google.com/gsi/client');
      await this.loadGapi();

      this.tokenClient = window.google.accounts.oauth2.initTokenClient({
        client_id: CLIENT_ID,
        scope: SCOPES,
      });
    });
  }

  private async loadGapi() {
    return new Promise<void>(resolve => {
      window.gapi.load('picker', resolve);
    });
  }

  public async uploadFile(
    file: GoogleDriveFile,
    galleryId: string,
    sectionId: string,
    position: number,
    assetToReplace?: AssetDto
  ): Promise<AssetDto> {
    return firstValueFrom(
      this.http
        .post<AssetDto>(this.config.internalApiUrlBase + '/upload-from-google-drive?with-auth=Server5Minute', {
          gallery: galleryId,
          section: sectionId,
          asset_name: file.name,
          position,
          id: file.id,
          content_type: getFileContentType(file),
          asset_to_replace: assetToReplace?.id,
          google_drive_token: this.accessToken,
        })
        .pipe(
          catchError(err => {
            if (err instanceof HttpErrorResponse) {
              if (err.status === 503) {
                return timer(10_000).pipe(map(() => undefined));
              }
            }
            return throwError(() => err);
          })
        )
    );
  }

  private showPicker(): Promise<GoogleDriveFile[]> {
    return new Promise(resolve => {
      const picker = new window.google.picker.PickerBuilder()
        .addView(window.google.picker.ViewId.DOCS_IMAGES_AND_VIDEOS)
        .enableFeature(window.google.picker.Feature.MULTISELECT_ENABLED)
        .enableFeature(window.google.picker.Feature.SUPPORT_DRIVES)
        .enableFeature(window.google.picker.Feature.SUPPORT_TEAM_DRIVES)
        .setOAuthToken(this.accessToken)
        .setDeveloperKey(API_KEY)
        .setCallback(data => {
          if (data.action == 'picked') {
            resolve(
              data.docs.map(doc => {
                return <GoogleDriveFile>{
                  name: doc.name,
                  size: doc.sizeBytes,
                  id: doc.id,
                  source: 'google-drive',
                };
              })
            );
          } else if (data.action == 'cancel') {
            resolve([]);
          }
        })
        .build();
      picker.setVisible(true);
    });
  }

  public async startChooser(): Promise<GoogleDriveFile[]> {
    await this.loadGoogleClient();
    return new Promise(resolve => {
      // Request an access token.
      this.tokenClient.callback = async response => {
        if (response.error !== undefined) {
          throw response;
        }
        this.accessToken = response.access_token;
        const resp = await this.showPicker();
        resolve(resp);
      };

      if (this.accessToken === null) {
        // Prompt the user to select a Google Account and ask for consent to share their data
        // when establishing a new session.
        this.tokenClient.requestAccessToken({ prompt: 'consent' });
      } else {
        // Skip display of account chooser and consent dialog for an existing session.
        this.tokenClient.requestAccessToken({ prompt: '' });
      }
    });
  }
}
